import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ClubChallengesSection } from '@/components/challenges'
import { ClubFormModal, ClubInviteCard, ClubMembersCard } from '@/components/clubs'
import { TravelingBooksSection } from '@/components/traveling'
import { Button, ConfirmDialog } from '@/components/ui'
import { useAuth } from '@/contexts/auth'
import { useClub, useDeleteClub, useRemoveClubMember, useUpdateClub } from '@/hooks/clubs'

export function ClubPage() {
  const { t } = useTranslation()
  const { clubId } = useParams<{ clubId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: club, isLoading } = useClub(clubId)
  const updateClub = useUpdateClub()
  const deleteClub = useDeleteClub()
  const leaveClub = useRemoveClubMember()
  const [editing, setEditing] = useState(false)
  const [confirm, setConfirm] = useState<'leave' | 'delete' | null>(null)

  if (isLoading) {
    return <div className="h-40 animate-pulse rounded-lg bg-muted" />
  }

  if (!club || !clubId) {
    return (
      <div className="py-12 text-center">
        <p className="mb-4 text-muted-foreground">{t('clubs.notFound')}</p>
        <Link to="/dashboard/clubs" className="text-primary hover:underline">
          {t('clubs.backToClubs')}
        </Link>
      </div>
    )
  }

  const isAdmin = club.my_role === 'admin'

  return (
    <div className="space-y-6">
      <div>
        <Link to="/dashboard/clubs" className="text-sm text-muted-foreground hover:text-foreground">
          ← {t('clubs.backToClubs')}
        </Link>
        <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="break-words text-3xl font-bold text-foreground">{club.name}</h1>
            {club.description && (
              <p className="mt-1 whitespace-pre-wrap break-words text-muted-foreground">
                {club.description}
              </p>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                {t('common.edit')}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setConfirm('leave')}>
              {t('clubs.leave')}
            </Button>
            {isAdmin && (
              <Button variant="destructive" size="sm" onClick={() => setConfirm('delete')}>
                {t('common.delete')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2 min-w-0" id="club-main">
          <ClubChallengesSection clubId={clubId} />
          <TravelingBooksSection clubId={clubId} />
        </div>
        <div className="space-y-6 min-w-0">
          <ClubMembersCard clubId={clubId} isAdmin={isAdmin} />
          <ClubInviteCard clubId={clubId} clubName={club.name} isAdmin={isAdmin} />
        </div>
      </div>

      {editing && (
        <ClubFormModal
          title={t('clubs.edit')}
          submitLabel={t('common.save')}
          initial={club}
          isPending={updateClub.isPending}
          error={updateClub.isError}
          onClose={() => setEditing(false)}
          onSubmit={async (values) => {
            await updateClub.mutateAsync({ clubId, ...values })
            setEditing(false)
          }}
        />
      )}

      <ConfirmDialog
        isOpen={confirm !== null}
        title={confirm === 'delete' ? t('clubs.deleteTitle') : t('clubs.leaveTitle')}
        message={
          confirm === 'delete'
            ? t('clubs.deleteMessage', { club: club.name })
            : t('clubs.leaveMessage', { club: club.name })
        }
        confirmText={confirm === 'delete' ? t('common.delete') : t('clubs.leave')}
        variant="danger"
        isLoading={deleteClub.isPending || leaveClub.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          if (confirm === 'delete') await deleteClub.mutateAsync(clubId)
          else if (user) await leaveClub.mutateAsync({ clubId, userId: user.id })
          setConfirm(null)
          navigate('/dashboard/clubs')
        }}
      />
    </div>
  )
}
